import {
  logout,
  selectAuthLoading,
  selectSchoolId,
  selectUser,
  selectUserRole,
  updateSchool
} from "@/store/slices/authSlice";
import type { AppDispatch } from "@/store";
import {
  canAccessNavItem,
  CATALOG_ADMIN_ROLES,
  getInitials,
  hasMenuPermission,
  ROLE_PERMISSIONS,
  ROLES,
} from "@/utils/constants";
import { MyClassNavIcon, SchoolSelectorIcon } from "@/utils/svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  BookOpenText,
  ChevronDown,
  ClipboardList,
  Layers,
  FileClock,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Menu,
  School,
  User,
  Users2,
  X,
} from "lucide-react";
import { Fragment, use, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../design-system/button";
import Modal from "../design-system/Modal";

import Notification from "../notifications/Notification";

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  permissions: string[];
  /** When set, item is shown only for these role codes (e.g. Super Admin catalog admin) */
  roles?: string[];
  hasDropdown?: boolean;
  dropdownItems?: NavigationItem[];
}

type School = {
  id: string;
  name: string;
};

function Header() {
  // State to track which navigation item is active
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useSelector(selectAuthLoading);
  const userRole = useSelector(selectUserRole);

  const loggedUser = useSelector(selectUser);
  const schoolId = useSelector(selectSchoolId);

  const schoolList: any[] = loggedUser?.school_list || [];
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (schoolList?.length) {
      const matchedSchool = schoolList.find((sch) => sch.id === schoolId) || schoolList[0];
      setSelectedSchool(matchedSchool);
    }
  }, [schoolList]);

  const handleSelect = (school: {
    id: string;
    name: string;
  }) => {
    if (school) {
      setSelectedSchool(school);
      setOpen(false);
      const schoolData: any = { ...loggedUser, school_id: school?.id };
      dispatch(updateSchool(schoolData)); // Update school_id in the Redux store
    }
  };

  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const allNavigationItems: NavigationItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/dashboard",
      icon: <LayoutDashboard className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_DASHBOARD],
    },
    {
      id: "school",
      label: "School Management",
      path: "/school-management",
      icon: <School className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_SCHOOL_MANAGEMENT],
    },
    ...(loggedUser?.roleName === "Student" ||
    loggedUser?.roleName === "Teacher"
      ? [
          {
            id: "course",
            label: "My Courses",
            path: "/course-management",
            icon: <BookOpenText className="w-[30px] h-[30px]" />,
            permissions: [ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT],
          },
        ]
      : [
          {
            id: "programs",
            label: "Programs",
            path: "/course-management",
            icon: <ClipboardList className="w-[30px] h-[30px]" />,
            hasDropdown: true,
            permissions: [
              ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
              ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
            ],
            dropdownItems: [
              {
                id: "course-management",
                label: "Course Management",
                path: "/course-management",
                icon: <ClipboardList style={{ width: "26px", height: "26px" }} />,
                permissions: [ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT],
              },
              {
                id: "class-management",
                label: "Class Management",
                path: "/class-management",
                icon: <Layers style={{ width: "26px", height: "26px" }} />,
                permissions: [
                  ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                  ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
                ],
                roles: CATALOG_ADMIN_ROLES,
              },
              {
                id: "level-management",
                label: "Level Management",
                path: "/level-management",
                icon: <BookOpenText style={{ width: "26px", height: "26px" }} />,
                permissions: [
                  ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                  ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
                ],
                roles: CATALOG_ADMIN_ROLES,
              },
            ],
          },
        ]),
    {
      id: "user-management",
      label: "User Management",
      path: "/user-management",
      icon: <School className="w-[30px] h-[30px]" />,
      hasDropdown: true,
      permissions: [ROLE_PERMISSIONS.TAB_USER_MANAGEMENT],
      dropdownItems: [
        {
          id: "student-management",
          label: "Student",
          path: "/user-management/student",
          icon: <GraduationCap style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_USER_MANAGEMENT],
        },
        {
          id: "teacher-management",
          label: "Teacher",
          path: "/user-management/teacher",
          icon: <User style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_USER_MANAGEMENT],
        },
      ],
    },
    {
      id: "assignments",
      label: "Assignment Management",
      path: "/assignments",
      icon: <ClipboardList className="w-[30px] h-[30px]" />,
      hasDropdown: true,
      permissions: [
        ROLE_PERMISSIONS.TAB_ASSIGNMENT_MANAGEMENT,
        ROLE_PERMISSIONS.TAB_SUBMISSION_MANAGEMENT,
      ],
      dropdownItems: [
        {
          id: "assignment-management",
          label: "Assignment",
          path: "/assignments",
          icon: <GraduationCap style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_ASSIGNMENT_MANAGEMENT],
        },
        {
          id: "submission-management",
          label: "Submission",
          path: "/submissions",
          icon: <User style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_SUBMISSION_MANAGEMENT],
        },
      ],
    },
    // {
    //   id: "submission",
    //   label: "Submission Management",
    //   path: "/submission-management",
    //   icon: <School className="w-[30px] h-[30px]" />,
    //   permissions: ROLE_PERMISSIONS.TAB_SUBMISSION_MANAGEMENT,
    // },
    // {
    //   id: "assignment",
    //   label: "Assignment",
    //   path: "/assignments",
    //   icon: <ClipboardList className="w-[30px] h-[30px]" />,
    //   permissions: ROLE_PERMISSIONS.TAB_ASSIGNMENT,
    // },
    {
      id: "student-log",
      label: "Student Log",
      path: "/student-log",
      icon: <GraduationCap className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_STUDENT_LOG],
    },
    {
      id: "my-courses",
      label: "My Courses",
      path: "/my-courses",
      icon: <LayoutDashboard className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_MY_COURSES],
    },
    {
      id: "my-class",
      label: "My Class",
      path: "/my-class",
      icon: <MyClassNavIcon className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_MY_CLASS],
    },
    {
      id: "feedback",
      label: "Feedbacks",
      path: "/feedbacks",
      icon: <School className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_FEEDBACK],
    },
    {
      id: "activity",
      label: "Activity Log",
      path: "/activity-log",
      hasDropdown: true,
      icon: <FileClock className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_ACTIVITY_LOG],
      dropdownItems: [
        {
          id: "student-activity-log",
          label: "Student Log",
          path: "/activity/student-logs",
          icon: <GraduationCap style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_ACTIVITY_STUDENT],
        },
        {
          id: "school-activity-log",
          label: "School Log",
          path: "/activity/school-logs",
          icon: <School style={{ width: "26px", height: "26px" }} />,
          permissions: [ROLE_PERMISSIONS.TAB_ACTIVITY_SCHOOL],
        },
      ],
    },
    {
      id: "group-school-admin",
      label: "Group School Admin",
      path: "/group-school-admin",
      icon: <Users2 className="w-[30px] h-[30px]" />,
      permissions: [ROLE_PERMISSIONS.TAB_GROUP_SCHOOL_ADMIN],
    },
  ];

  // Handle logout
  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // Dispatch logout action with navigate function
      dispatch(logout(navigate));
      // Modal will be closed automatically when redirected
    } catch (error) {
      console.error("Logout failed:", error);
      // setLogoutModalOpen(false);
      // Still navigate to sign in page even if logout API fails
      // navigate("/auth/signin");
    }
  };

  const handleLogoutCancel = () => {
    setLogoutModalOpen(false);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    if (!open) {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
  };

  const userRoleCode = userRole?.code ?? "";
  const userPerms = (userRole?.permissions as string[]) ?? [];

  // Filter navigation: permissions (OR) + optional roles; drop empty dropdown parents
  const navigationItems = allNavigationItems
    .map((item) => {
      if (!item.hasDropdown || !item.dropdownItems) return item;
      return {
        ...item,
        dropdownItems: item.dropdownItems.filter((child) =>
          canAccessNavItem(userPerms, userRoleCode, child)
        ),
      };
    })
    .filter((item) => {
      if (!canAccessNavItem(userPerms, userRoleCode, item)) return false;
      if (
        item.hasDropdown &&
        (!item.dropdownItems || item.dropdownItems.length === 0)
      ) {
        return false;
      }
      return true;
    });

  const isStudentOrTeacher =
    userRole?.code === ROLES.STUDENT || userRole?.code === ROLES.TEACHER;

  // Roles whose header is space-constrained at 90–100% browser zoom get a
  // compacted desktop nav so every item stays visible instead of collapsing to
  // the hamburger. Super Admin has the most items (5) so it is the tightest;
  // Group School Admin (3 items + a school dropdown) only needs a small trim,
  // and its school dropdown is left untouched.
  const isSuperAdmin = loggedUser?.roleName === "Super Admin";
  const isGroupSchoolAdmin = loggedUser?.roleName === "Group School Admin";

  const navClass = isSuperAdmin
    ? "gap-[4px] min-[1200px]:flex [&_a]:px-[8px] [&_a]:gap-1.5 [&_button]:px-[8px] [&_button]:gap-1.5 [&_svg]:w-6 [&_svg]:h-6 [&_span]:text-[15px]"
    : isGroupSchoolAdmin
      ? "gap-[6px] xl:flex [&_a]:px-[11px] [&_a]:gap-1.5 [&_button]:px-[11px] [&_button]:gap-1.5 [&_span]:text-[17px]"
      : "gap-[8px] xl:flex";

  const headerLeftGap = isSuperAdmin ? "gap-4" : isGroupSchoolAdmin ? "gap-6" : "gap-8";
  const mobileMenuHidden = isSuperAdmin ? "min-[1200px]:hidden" : "xl:hidden";

  return (
    <header className="bg-white h-[82px] border-b border-gray-200 px-4 md:px-[42px] py-1 flex-shrink-0 shadow-[0px_4px_12px_0px_#00000014]">
      <div className="flex items-center h-full justify-between">
        {/* Logo and Navigation */}
        <div className={`flex items-center ${headerLeftGap}`}>
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => window.location.reload()}
          >
            <img
              src={"/images/header-logo.png"}
              alt="Music Temple"
              className="w-[84px] h-[62px] object-contain"
            />
          </div>

          {/* Mobile Menu Button - Visible on smaller screens */}
          <button
            className={`${mobileMenuHidden} text-gray-600 focus:outline-none`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>

          {/* Desktop Navigation - Only visible on screens wider than 1380px */}
          <nav className={`hidden items-center ${navClass}`}>
            {navigationItems.map((item) => {
              if (!item.hasDropdown) {
                return (
                  <Link
                    to={item.path}
                    key={item.id}
                    className={`flex h-[42px] items-center gap-2 px-[13.5px] py-[10px] rounded-[8px] text-h3-d transition-all duration-200 ${location.pathname === item.path ||
                      location.pathname.startsWith(item.path)
                      ? "text-white shadow-md"
                      : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                      }`}
                    style={{
                      background:
                        location.pathname === item.path ||
                          location.pathname.startsWith(item.path)
                          ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                          : "",
                    }}
                  >
                    {item.icon}
                    <span
                      className="whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-[18px]"
                      style={{
                        lineHeight: "25px",
                      }}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              } else {
                return (
                  <DropdownMenu
                    key={item.id}
                    onOpenChange={handleDropdownOpenChange}
                  >
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`group flex h-[42px] items-center gap-2 px-[13.5px] py-[10px] rounded-[8px] text-h3-d transition-all duration-200 ${location.pathname === item.path ||
                          item.dropdownItems?.some(
                            (dropItem) =>
                              location.pathname === dropItem.path ||
                              location.pathname.startsWith(`${dropItem.path}/`)
                          ) ||
                          location.pathname.startsWith(item.path)
                          ? "text-white shadow-md"
                          : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                          }`}
                        style={{
                          background:
                            location.pathname === item.path ||
                              item.dropdownItems?.some(
                                (dropItem) =>
                                  location.pathname === dropItem.path ||
                                  location.pathname.startsWith(`${dropItem.path}/`)
                              ) ||
                              location.pathname.startsWith(item.path)
                              ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                              : "",
                        }}
                      >
                        {item.icon}
                        <span
                          className="whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-[18px]"
                          style={{
                            lineHeight: "25px",
                          }}
                        >
                          {item.label}
                        </span>
                        <ChevronDown className="w-[24px] h-[24px] ml-1 flex-shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="min-w-[253px] bg-white rounded-[8px] shadow-[0px_4px_12px_0px_#00000014] overflow-hidden"
                      sideOffset={5}
                      align="center"
                    >
                      {item.dropdownItems?.map((dropdownItem) => (
                        <>
                          {canAccessNavItem(
                            userPerms,
                            userRoleCode,
                            dropdownItem
                          ) && (
                              <Link to={dropdownItem.path} key={dropdownItem.id}>
                                <DropdownMenuItem
                                  className={`flex items-center w-[270px] m-[10px] h-[42px] rounded-[8px] text-left hover:bg-[#F5F5F5] outline-none cursor-pointer transition-colors duration-150`}
                                  style={{
                                    background:
                                      location.pathname === dropdownItem.path ||
                                        location.pathname.startsWith(
                                          dropdownItem.path
                                        )
                                        ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                                        : "",
                                  }}
                                >
                                  <div
                                    className={`p-1 text-label1-d flex items-center gap-2 ${location.pathname === dropdownItem.path ||
                                      location.pathname.startsWith(
                                        dropdownItem.path
                                      )
                                      ? "text-white"
                                      : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                                      }`}
                                  >
                                    {dropdownItem.icon}

                                    <span
                                      className="font-semibold text-[18px]"
                                      style={{
                                        lineHeight: "25px",
                                      }}
                                    >
                                      {dropdownItem.label}
                                    </span>
                                  </div>
                                </DropdownMenuItem>
                              </Link>
                            )}
                        </>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
            })}
          </nav>
        </div>

        <div className="flex items-center gap-8">
          {/* Notification Button */}

          {isStudentOrTeacher &&
            hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_NOTIFICATION,
              userRole?.permissions as string[]
            ) && <Notification />}

          {/* School Dropdown */}
          {schoolList?.length > 0 && (
            <div className="relative w-full min-w-[234px] max-w-[400px]">
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full bg-white border border-gray-300 rounded-[12px] px-2 py-[10px] flex items-center justify-between gap-3 shadow-sm min-w-0"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-[32px] h-[32px] shrink-0 rounded-full bg-[#F5A623] flex items-center justify-center">
                    {/* <SchoolSelectorIcon /> */}
                    <School className="text-white" />
                  </div>

                  <div className="text-left min-w-0 flex-1">
                    <p className="text-xs text-gray-400 leading-tight">
                      School name
                    </p>
                    <p
                      className="text-sm font-medium text-gray-800 truncate"
                      title={selectedSchool?.name}
                    >
                      {selectedSchool?.name}
                    </p>
                  </div>
                </div>

                <ChevronDown
                  className={`w-5 h-5 shrink-0 text-gray-600 transition-transform duration-200 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>

              {open && (
                <div className="absolute left-0 right-0 mt-2 w-full max-w-[400px] bg-white border border-gray-200 rounded-[8px] shadow-lg overflow-hidden z-50">
                  <div className="max-h-64 overflow-y-auto overscroll-contain">
                    {schoolList.map((school) => (
                      <button
                        key={school.id}
                        type="button"
                        onClick={() => handleSelect(school)}
                        className={`w-full min-w-0 text-left px-5 py-3 border-b border-[#E5E9EB] last:border-b-0 transition-colors hover:bg-gray-100 ${
                          selectedSchool?.id === school.id
                            ? "bg-[#E8F1FA] font-medium text-gray-900"
                            : "text-gray-700"
                        }`}
                      >
                        <span
                          className="block text-sm truncate"
                          title={school.name}
                        >
                          {school.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Profile */}
          <DropdownMenu onOpenChange={handleDropdownOpenChange}>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center gap-3 cursor-pointer w-auto max-w-[234px] h-[56px] bg-[#F5FBFF] rounded-4xl p-2">
                <div className="w-[40px] h-[40px] bg-gray-200 text-[#1F1F1F] rounded-full flex items-center justify-center">
                  {getInitials(loggedUser?.name || "")}
                </div>

                <div className="hidden md:flex items-center gap-4">
                  <div className="text-left">
                    <div
                      className="text-[16px] font-medium text-[#1F1F1F] max-w-[110px] truncate"
                      title={loggedUser?.name}
                      style={{
                        lineHeight: "25px",
                      }}
                    >
                      {loggedUser?.name}
                    </div>
                    <div
                      className="text-[14px] font-normal text-[#292D3270] max-w-[110px] truncate"
                      style={{
                        lineHeight: "19px",
                      }}
                    >
                      {loggedUser?.roleName}
                    </div>
                  </div>
                  <ChevronDown className="w-[16px] h-[16px] text-borderBlack transition-transform duration-200 group-data-[state=open]:rotate-180 mr-4" />
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[162px] bg-white rounded-[8px] shadow-[0px_4px_12px_0px_#00000014] overflow-hidden"
              align="end"
            >
              {hasMenuPermission(
                ROLE_PERMISSIONS.ACTION_LOGOUT,
                userRole?.permissions as string[]
              ) && (
                  <DropdownMenuItem
                    className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-[#F5F5F5] outline-none cursor-pointer transition-colors duration-150"
                    onClick={handleLogoutClick}
                  >
                    <LogOut className="w-[20px] h-[20px] text-[#7B6CB8]" />
                    <span className="text-label1-d text-borderBlack">Logout</span>
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`${loggedUser?.roleName === "Super Admin" ? "min-[1460px]:hidden" : "xl:hidden"} fixed inset-0 z-10 bg-black/50 transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`bg-white h-screen w-4/5 max-w-sm overflow-y-auto shadow-xl transform transition-all duration-300 ease-in-out ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-[40px] h-[40px] bg-gray-200 text-[#1F1F1F] rounded-full flex items-center justify-center">
                {getInitials(loggedUser?.name || "")}
              </div>

              <div className="text-left">
                <div
                  className="text-[16px] font-medium text-[#1F1F1F]"
                  style={{
                    lineHeight: "22px",
                  }}
                >
                  {loggedUser?.name}
                </div>
                <div
                  className="text-[14px] font-normal text-[#292D3270]"
                  style={{
                    lineHeight: "19px",
                  }}
                >
                  {loggedUser?.roleName}
                </div>
              </div>
            </div>
          </div>

          <nav className="p-5 space-y-4">
            {navigationItems.map((item) => {
              if (!item.hasDropdown) {
                return (
                  <Link
                    to={item.path}
                    key={item.id}
                    className={`flex h-[42px] items-center gap-2 px-[13.5px] py-[10px] rounded-[8px] text-h3-d transition-all duration-200 ${location.pathname === item.path ||
                      location.pathname.startsWith(item.path)
                      ? "text-white shadow-md"
                      : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                      }`}
                    style={{
                      background:
                        location.pathname === item.path ||
                          location.pathname.startsWith(item.path)
                          ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                          : "",
                    }}
                  >
                    {item.icon}
                    <span
                      className="whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-[18px]"
                      style={{
                        lineHeight: "25px",
                      }}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              } else {
                return (
                  <div key={item.id} className="space-y-2">
                    <div
                      className={`flex h-[42px] items-center gap-2 px-[13.5px] py-[10px] rounded-[8px] text-h3-d transition-all duration-200 ${location.pathname === item.path ||
                        item.dropdownItems?.some(
                          (dropItem) =>
                            location.pathname === dropItem.path ||
                            location.pathname.startsWith(`${dropItem.path}/`)
                        ) ||
                        location.pathname.startsWith(item.path)
                        ? "text-white shadow-md"
                        : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                        }`}
                      style={{
                        background:
                          location.pathname === item.path ||
                            item.dropdownItems?.some(
                              (dropItem) =>
                                location.pathname === dropItem.path ||
                                location.pathname.startsWith(`${dropItem.path}/`)
                            ) ||
                            location.pathname.startsWith(item.path)
                            ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                            : "",
                      }}
                    >
                      {item.icon}
                      <span
                        className="whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-[18px]"
                        style={{
                          lineHeight: "25px",
                        }}
                      >
                        {item.label}
                      </span>
                    </div>

                    <div className="ml-10 space-y-2">
                      {item.dropdownItems?.map((dropdownItem) => (
                        <Fragment key={dropdownItem.id}>
                          {canAccessNavItem(
                            userPerms,
                            userRoleCode,
                            dropdownItem
                          ) && (
                              <Link
                                to={dropdownItem.path}
                                key={dropdownItem.id}
                                className={`flex items-center w-[253px] h-[62px] rounded-[8px] text-left hover:bg-[#F5F5F5] outline-none cursor-pointer transition-colors duration-150`}
                                style={{
                                  background:
                                    location.pathname === dropdownItem.path ||
                                      location.pathname.startsWith(
                                        dropdownItem.path
                                      )
                                      ? "linear-gradient(75.92deg, #795FA9 45.79%, #5E87C8 88.36%)"
                                      : "",
                                }}
                              >
                                <div
                                  className={`px-3 py-1 text-label1-d flex items-center gap-2 ${location.pathname === dropdownItem.path ||
                                    location.pathname.startsWith(
                                      dropdownItem.path
                                    )
                                    ? "text-white"
                                    : "text-[#c0c0c0] hover:text-[#1F1F1F]"
                                    }`}
                                >
                                  {dropdownItem.icon}
                                  <span
                                    className="font-semibold text-[18px]"
                                    style={{
                                      lineHeight: "25px",
                                    }}
                                  >
                                    {dropdownItem.label}
                                  </span>
                                </div>
                              </Link>
                            )}
                        </Fragment>
                      ))}
                    </div>
                  </div>
                );
              }
            })}
          </nav>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={logoutModalOpen}
        onClose={handleLogoutCancel}
        title="Logout"
        // closeOnOutsideClick={true}
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Logout?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={handleLogoutCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={handleLogoutConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Logging out..." : "Yes, Logout"}
          </Button>
        </div>
      </Modal>
    </header>
  );
}

export default Header;
